import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatError } from '../../lib/api';
import { startGoogleRedirect } from '../../lib/googleAuth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, Lock, Eye, EyeOff } from 'lucide-react';

function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

/**
 * Dialog shown when a guest user tries to complete a purchase.
 * Offers: (1) Google OAuth (primary)  (2) Email/password (expandable)
 * Once the user is authenticated, `onAuthenticated` is invoked so the caller
 * can resume the purchase flow.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function LoginRequiredDialog({ open, onClose, onAuthenticated, title, description }) {
  const { login, register } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [error, setError] = useState('');

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Google = full-page redirect. Stash the current page so we land back here
  // after auth and the caller can resume the purchase flow (the dialog's
  // `onAuthenticated` can't fire post-redirect, but the page auto-detects the
  // authenticated state on return and re-enables the purchase button).
  const handleGoogleLogin = () => {
    setError('');
    setGoogleLoading(true);
    try {
      const returnTo = window.location.pathname + window.location.search;
      startGoogleRedirect(returnTo);
    } catch {
      setError('Google sign-in is not available. Please try another method.');
      setGoogleLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailLoading(true);
    try {
      const userData = isRegister
        ? await register(name, email, password)
        : await login(email, password);
      onAuthenticated && onAuthenticated(userData);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || (isRegister ? 'Registration failed' : 'Login failed'));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setShowEmailForm(false);
    onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-md"
        data-testid="login-required-dialog"
        style={{
          background: 'var(--surface)',
          color: 'var(--text)',
          borderColor: 'var(--border)',
          boxShadow: '0 20px 48px rgba(0,0,0,0.35)',
        }}
      >
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
            style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
            <Lock className="w-5 h-5" />
          </div>
          <DialogTitle style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            {title || 'Sign in to complete purchase'}
          </DialogTitle>
          <DialogDescription className="text-sm" style={{ color: 'var(--muted-fg)' }}>
            {description || 'Sign in so we can securely store your transactions and sync your progress across devices.'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="p-3 rounded-lg text-sm mt-1" data-testid="login-dialog-error"
            style={{ background: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' }}>
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 mt-1">
          <button onClick={handleGoogleLogin}
            disabled={googleLoading || emailLoading}
            data-testid="dialog-google-login-btn"
            className="w-full py-3 rounded-lg font-semibold transition-all hover:shadow-md active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-60"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}>
            {googleLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <GoogleIcon />}
            {googleLoading ? 'Redirecting to Google…' : 'Continue with Google'}
          </button>

          <button onClick={() => setShowEmailForm((v) => !v)}
            data-testid="dialog-toggle-email"
            className="text-xs font-medium self-center px-2 py-1 rounded transition-all hover:opacity-80"
            style={{ color: 'var(--muted-fg)' }}>
            {showEmailForm ? 'Hide email sign in' : 'Or sign in with email'}
          </button>

          {showEmailForm && (
            <form onSubmit={handleEmailSubmit} className="flex flex-col gap-2.5 animate-fade-in">
              {isRegister && (
                <input type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)}
                  required data-testid="dialog-email-name"
                  className="w-full px-3 py-2.5 rounded-lg text-sm"
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} />
              )}
              <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="dialog-email-input"
                className="w-full px-3 py-2.5 rounded-lg text-sm"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} />
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} placeholder="Password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required data-testid="dialog-password-input"
                  className="w-full px-3 py-2.5 pr-10 rounded-lg text-sm"
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                  style={{ color: 'var(--muted-fg)' }}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button type="submit" disabled={emailLoading} data-testid="dialog-email-submit"
                className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
                {emailLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isRegister ? 'Create account' : 'Sign in'}
              </button>
              <button type="button" onClick={() => { setIsRegister((v) => !v); setError(''); }}
                className="text-xs self-center" style={{ color: 'var(--muted-fg)' }}
                data-testid="dialog-toggle-register">
                {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
